import { useCallback, useEffect, useRef } from 'react';
import { DISPLAY_MODES } from '../constants/common';
import {
  CHAT_EVENTS,
  EMBED_MESSAGE_TYPE,
  HOST_EVENTS,
  MESSAGE_SOURCES,
  REQUEST_TYPES,
  SITE_MODIFICATION_STATUSES,
} from '../constants/messages';
import toHostMessage from '../helpers/to-host-message';

const isKnownDisplayMode = (mode) => Object.values(DISPLAY_MODES).includes(mode);

// Only messages in the shared envelope sent by the chat; our own (`source:
// 'host'`), old-format and unrelated messages are dropped.
const isChatMessage = (message) =>
  Boolean(message) &&
  typeof message === 'object' &&
  message.type === EMBED_MESSAGE_TYPE &&
  Boolean(message.meta) &&
  typeof message.meta === 'object' &&
  message.meta.source === MESSAGE_SOURCES.CHAT;

const noop = () => {};

/**
 * Two-way messaging with the chat app running inside the iframe. The wire
 * contract lives in constants/messages.js.
 *
 * The chat app owns its own chrome, so it is the one that asks the editor to
 * open, close or switch display mode. The editor reports its state back as a
 * full `state` snapshot, so the chat can render the matching affordance and
 * knows which page "this page" refers to.
 *
 * `hostState` drives the `state` pushes and must keep its identity while
 * nothing changed; `getHostState` reads it fresh for `response`, which may be
 * sent before this component re-renders with the result of a request.
 */
const useAiChatInteractions = ({
  iframeRef,
  chatOrigin,
  hostState,
  getHostState,
  onChatReady = noop,
  onSetOpen,
  onSetDisplayMode,
  onSave,
  onSiteModificationStart = noop,
  onSiteModificationEnd = noop,
  onReload,
}) => {
  // Request ids already answered, or being answered: each gets one response.
  const seenRequestIdsRef = useRef(new Set());

  const postToChat = useCallback(
    (outbound) => {
      const chatWindow = iframeRef.current && iframeRef.current.contentWindow;

      if (!chatWindow || !chatOrigin) return;

      chatWindow.postMessage(toHostMessage(outbound), chatOrigin);
    },
    [iframeRef, chatOrigin]
  );

  const postState = useCallback(
    (state) => postToChat({ event: HOST_EVENTS.STATE, payload: state }),
    [postToChat]
  );

  const handleRequest = useCallback(
    async (id, { type }) => {
      const handlers = {
        [REQUEST_TYPES.SAVE]: onSave,
      };
      const handler = handlers[type];
      let result;

      if (!handler) {
        result = { ok: false };
      } else {
        try {
          await handler();
          result = { ok: true };
        } catch (e) {
          result = { ok: false, ...(e && e.message && { error: e.message }) };
        }
      }

      postToChat({
        event: HOST_EVENTS.RESPONSE,
        id,
        payload: { type, ...result, state: getHostState() },
      });
    },
    [postToChat, getHostState, onSave]
  );

  useEffect(() => {
    if (!chatOrigin) return undefined;

    const handleMessage = (event) => {
      const chatWindow = iframeRef.current && iframeRef.current.contentWindow;

      if (!chatWindow || event.source !== chatWindow) return;
      if (event.origin !== chatOrigin) return;

      const message = event.data;

      if (!isChatMessage(message)) return;

      const { meta } = message;
      const payload = message.payload || {};

      switch (meta.event) {
        case CHAT_EVENTS.READY:
          postState(getHostState());
          onChatReady();
          break;
        case CHAT_EVENTS.SET_VIEW:
          if (typeof payload.isOpen === 'boolean') {
            onSetOpen(payload.isOpen);
          }
          if (isKnownDisplayMode(payload.displayMode)) {
            onSetDisplayMode(payload.displayMode);
          }
          break;
        case CHAT_EVENTS.REQUEST: {
          const { id } = meta;

          if (!id || seenRequestIdsRef.current.has(id)) break;

          seenRequestIdsRef.current.add(id);
          handleRequest(id, payload);
          break;
        }
        case CHAT_EVENTS.SITE_MODIFICATION:
          if (payload.status === SITE_MODIFICATION_STATUSES.START) {
            onSiteModificationStart();
          } else if (payload.status === SITE_MODIFICATION_STATUSES.END) {
            onSiteModificationEnd({ changed: payload.changed === true });
          }
          break;
        case CHAT_EVENTS.RELOAD:
          onReload();
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    iframeRef,
    chatOrigin,
    postState,
    getHostState,
    handleRequest,
    onChatReady,
    onSetOpen,
    onSetDisplayMode,
    onSiteModificationStart,
    onSiteModificationEnd,
    onReload,
  ]);

  useEffect(() => {
    postState(hostState);
  }, [postState, hostState]);
};

export default useAiChatInteractions;
